<script setup lang="ts">
import { computed, ref } from 'vue';
import { useStore } from 'vuex';
import { useToast } from 'vue-toastification';
import {
  declineAccountGeofenceRemovalSuggestion,
  getAccountGeofenceRemovalSuggestions,
  getAccountPlaceSuggestions,
  postAccountDialog,
} from '@gpx-spa/queries';
import ChatHeader from '@/components/ai/ChatHeader.vue';
import RemovalSuggestionCard from './RemovalSuggestionCard.vue';
import { useChatStore } from '@/stores/useChatStore';
import { useDialogMessagesStore } from '@/stores/useDialogMessagesStore';
import { useTypingStore } from '@/stores/useTypingStore';
import { buildCardSeedMessages } from '@/components/ai/buildCardSeedMessages';
import { getScoutDefaultModel } from '@gpx-spa/constants';

const emit = defineEmits<{ (e: 'close'): void }>();

const store = useStore();
const accountId = computed<number | null>(
  () => store.getters['auth/accountId'] ?? null,
);
const userId = computed<number | undefined>(
  () => store.getters['auth/user']?.id,
);

const chatStore = useChatStore();
const dialogMessagesStore = useDialogMessagesStore();
const typingStore = useTypingStore();
const toast = useToast();

const { suggestions, count, isLoading, isError } =
  getAccountGeofenceRemovalSuggestions(accountId);

const { count: placeSuggestionsCount } = getAccountPlaceSuggestions(accountId);

const dismissedIds = ref<Set<string>>(new Set());

const suggestionIds = computed<string[]>(() =>
  (suggestions.value ?? [])
    .map((s: { id: string }) => s.id)
    .filter((id: string) => !dismissedIds.value.has(id)),
);

const decline = declineAccountGeofenceRemovalSuggestion();

function onDismiss(id: string) {
  // Keep is host-owned + optimistic + restore-on-error, mirroring the
  // place-suggestions Dismiss pattern.
  dismissedIds.value = new Set([...dismissedIds.value, id]);
  const acctId = accountId.value;
  if (acctId == null) return;
  decline.mutate(
    { accountId: acctId, suggestionId: id },
    {
      onError: () => {
        const next = new Set(dismissedIds.value);
        next.delete(id);
        dismissedIds.value = next;
        toast.error('Could not dismiss suggestion. Please try again.');
      },
    },
  );
}

function onOpenPlaceSuggestions() {
  chatStore.openProactiveSuggestions();
}

const text = ref('');
const postDialog = postAccountDialog(accountId.value as number);
const submitting = computed(() => postDialog.isPending.value);
const sendDisabled = computed(
  () => submitting.value || text.value.trim().length === 0,
);

function send() {
  const trimmed = text.value.trim();
  if (!trimmed || submitting.value) return;
  if (suggestionIds.value.length === 0) return;

  postDialog.mutate(
    {
      messages: buildCardSeedMessages(
        {
          toolName: 'ui_removal_card',
          idsKey: 'removal_ids',
          label: 'geofence removal suggestion',
        },
        suggestionIds.value,
        trimmed,
      ),
      model: getScoutDefaultModel(),
      user_id: userId.value,
    },
    {
      onSuccess: (dialog: { id: string; messages?: unknown[] }) => {
        typingStore.startTyping(String(dialog.id));
        if (dialog.messages && dialog.messages.length > 0) {
          dialogMessagesStore.addMessages(dialog.id, dialog.messages);
        }
        chatStore.onNewDialogCreated(dialog);
        text.value = '';
      },
      onError: () => {
        toast.error('Could not start the dialog. Please try again.');
      },
    },
  );
}
</script>

<template>
  <div class="flex h-full flex-col">
    <ChatHeader
      title="Inactive geofences"
      :editable="false"
      @close="emit('close')"
    />
    <div class="flex-1 overflow-y-auto p-4">
      <div v-if="isLoading" class="flex h-full items-center justify-center">
        <div
          class="size-8 animate-spin rounded-full border-b-2 border-gpx_slate-100"
        />
      </div>
      <div
        v-else-if="isError || count === 0"
        class="flex h-full items-center justify-center text-center text-gpx_sm text-gpx_black-500"
      >
        No removal suggestions to show right now.
      </div>
      <div v-else class="flex flex-col gap-3">
        <p class="text-gpx_sm text-gpx_black-50">
          These geofences have seen little or no activity. Review and clean up
          your list.
        </p>
        <div class="flex flex-col gap-2">
          <RemovalSuggestionCard
            v-for="(id, index) in suggestionIds"
            :key="id"
            :suggestion-id="id"
            :rank="index + 1"
            @dismiss="onDismiss"
          />
        </div>
        <button
          v-if="placeSuggestionsCount > 0"
          type="button"
          class="mt-2 text-left text-gpx_xs text-gpx_terracotta-300 hover:underline"
          data-testid="removals-cross-link"
          @click="onOpenPlaceSuggestions"
        >
          You also have {{ placeSuggestionsCount }} suggested geofence{{
            placeSuggestionsCount === 1 ? '' : 's'
          }}
          to review.
        </button>
      </div>
    </div>
    <div class="flex flex-col gap-2 border-t border-gpx_gray-900 bg-white p-2">
      <div class="relative rounded-lg border border-gpx_gray-800 bg-white py-1">
        <textarea
          v-model="text"
          rows="2"
          placeholder="Ask Scout to dig deeper, or ignore — your call"
          class="w-full resize-none border-0 bg-transparent px-3 py-1 text-gpx_sm outline-none focus:outline-none"
          :disabled="submitting"
          data-testid="proactive-removals-input"
          @keydown.enter.exact.prevent="send"
        />
        <button
          type="button"
          class="absolute bottom-2 right-3 text-gpx_gray-200 hover:text-gpx_black-500 disabled:cursor-not-allowed disabled:opacity-50"
          :disabled="sendDisabled"
          data-testid="proactive-removals-send"
          @click="send"
        >
          <svg class="size-7" fill="#E0443E" viewBox="0 0 20 20">
            <path
              d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z"
            />
          </svg>
        </button>
      </div>
    </div>
  </div>
</template>
